/** "Get the app" sheet: how to put Ancient Nine on the home screen, for the player's own device first. */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { PLATFORM_ORDER, currentPlatform, promptInstall, useInstall } from '../lib/install';
import type { Platform } from '../lib/install';
import { IconDotsV, IconInstallScreen, IconShareBox } from './Icons';
import { Sheet } from './Sheet';

const share = (
  <span className="getapp-ic" role="img" aria-label="Share">
    <IconShareBox />
  </span>
);
const dots = (
  <span className="getapp-ic" role="img" aria-label="menu">
    <IconDotsV />
  </span>
);
const bar = (
  <span className="getapp-ic" role="img" aria-label="Install">
    <IconInstallScreen />
  </span>
);

const GUIDES: Record<Exclude<Platform, 'other'>, { label: string; steps: ReactNode[] }> = {
  'ios-safari': {
    label: 'iPhone or iPad, Safari',
    steps: [<>Tap {share} Share.</>, <>Choose <b>Add to Home Screen</b>.</>, <>Tap <b>Add</b>.</>],
  },
  'ios-chrome': {
    label: 'iPhone or iPad, Chrome',
    steps: [<>Tap {share} Share, in the address bar.</>, <>Choose <b>Add to Home Screen</b>.</>, <>Tap <b>Add</b>.</>],
  },
  android: {
    label: 'Android, Chrome',
    steps: [<>Tap {dots} at the top right.</>, <>Choose <b>Install app</b> or <b>Add to Home screen</b>.</>, <>Tap <b>Install</b>.</>],
  },
  'desktop-chrome': {
    label: 'Computer, Chrome or Edge',
    steps: [<>Click {bar} in the address bar, or {dots} then <b>Install Ancient Nine</b>.</>, <>Click <b>Install</b>.</>],
  },
  'desktop-safari': {
    label: 'Mac, Safari',
    steps: [<>Choose <b>File</b>, then <b>Add to Dock</b>.</>, <>Click <b>Add</b>.</>],
  },
};

function Guide({ id }: { id: Exclude<Platform, 'other'> }) {
  const g = GUIDES[id];
  return (
    <section className="getapp-guide">
      <h3 className="getapp-label">{g.label}</h3>
      <ol className="getapp-steps">
        {g.steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </section>
  );
}

export function GetApp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const here = currentPlatform();
  const { canPrompt } = useInstall();
  const [busy, setBusy] = useState(false);
  const first = here === 'other' ? null : here;
  const rest = PLATFORM_ORDER.filter((p) => p !== first) as Exclude<Platform, 'other'>[];

  const install = async () => {
    setBusy(true);
    const ok = await promptInstall();
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title="Get the app" labelledBy="getapp-title" className="getapp-sheet">
      <p className="getapp-lede">Keep it on your home screen, like any other fine instrument. It plays against the computer offline, too.</p>
      {canPrompt && (
        <button className="btn btn-gold btn-big wide-btn" onClick={() => void install()} disabled={busy} data-autofocus>
          Install
        </button>
      )}
      {first && <Guide id={first} />}
      <details className="getapp-other" {...(first ? {} : { open: true })}>
        <summary>Other devices</summary>
        {rest.map((p) => (
          <Guide key={p} id={p} />
        ))}
      </details>
    </Sheet>
  );
}
